/* stylelint-disable no-descending-specificity */
import styled, { css } from "styled-components"
import Icon from "components/atoms/Icon"
import { Link } from "gatsby-plugin-intl"
import Autocomplete from "components/atoms/Autocomplete"
import Dropdown from "components/atoms/Dropdown"
import Button from "components/atoms/Button"

const StyledWrapper = styled.div`
  display: flex;
  align-items: baseline;
  background: ${({ theme }) => theme.color.white};
  flex-wrap: wrap;
  padding: 2.4rem 0;
  flex-direction: column;

  ${({ theme }) => theme.mq.medium} {
    height: 13.6rem;
    flex-wrap: nowrap;
  }
`

const projectDropdownStyles = css`
  order: 4;

  ${({ theme }) => theme.mq.large} {
    margin: 0 4rem 0 0.8rem;
  }
`
const projectMiddleDropdownStyles = css`
  order: 4;

  ${({ theme }) => theme.mq.medium} {
    margin-left: 0.8rem;
  }
`

const iconStyles = css`
  width: 2.2rem;
  height: 2.2rem;
  padding: 0.8rem;
  border: solid 1px ${({ theme }) => theme.color.secondary04};
  box-sizing: content-box;
`

const StyledIcon = styled(Icon)`
  ${iconStyles}
`

const buttonStyles = css`
  cursor: pointer;
  order: 2;

  &:hover {
    background: ${({ theme }) => theme.color.main01};

    ${StyledIcon} {
      path#interior {
        fill: ${({ theme }) => theme.color.white};
      }
      polygon {
        fill: ${({ theme }) => theme.color.white};
      }
      #mask-2,
      #Oval {
        stroke: ${({ theme }) => theme.color.white}!important;
      }
    }
  }

  ${({ theme }) => theme.mq.medium} {
    order: 3;
    display: flex;
  }
`

const StyledResetWrapper = styled.div`
  ${buttonStyles}
  display: none;

  ${({ theme }) => theme.mq.medium} {
    margin-right: 1.7rem;
    display: flex;
  }
`

const StyledLink = styled(Link)`
  display: none;
  ${buttonStyles}

  ${({ mobile }) =>
    mobile &&
    css`
      display: flex;
      position: absolute;
      right: 0;
      top: 0.4rem;
    `}

  ${({ theme }) => theme.mq.medium} {
    ${({ mobile }) =>
      mobile &&
      css`
        display: none;
      `}
  }
`

const StyledFilterButton = styled.button`
  padding: 0;
  margin: 0;
  background: ${({ theme }) => theme.color.white};
  border: 0;
  outline: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.septenary03};
    outline-offset: 2px;
  }

  height: 4rem;
  margin-left: auto;

  svg {
    fill: ${({ theme }) => theme.color.secondary02};
  }
  ${buttonStyles}

  ${({ theme }) => theme.mq.medium} {
    display: none;
  }
`

// Preserves the original h3 visual size (font-family/weight/size/line-height
// as rendered by GlobalStyles' h3 tag selector + mixins.h3Sizes) now that
// this heading is semantically an h1 (the Portfolio page's only page heading).
const headingStyles = css`
  font-family: ${({ theme }) => theme.font.family.headers};
  font-weight: 700;
  font-size: 2rem;
  line-height: 2.4rem;

  ${({ theme }) => theme.mq.medium} {
    margin-right: 0;
    font-size: 3.2rem;
    line-height: 4rem;
  }
`
const StyledDropdownWrapper = styled.div`
  order: 3;
  flex: 100%;
  margin-top: 1.6rem;

  position: absolute;
  width: 100vw;
  z-index: 2;
  height: calc(100vh - 7.2rem);
  background: ${({ theme }) => theme.color.white};
  top: -100vh;
  left: 0;
  flex-direction: column;
  padding: 2.1rem 1.6rem;
  transition: top 0.5s;

  ${({ isVisible }) =>
    isVisible &&
    css`
      top: 7.2rem;
    `}

  ${({ theme }) => theme.mq.medium} {
    background: transparent;
    padding: 0;
    width: initial;
    height: initial;
    display: flex;
    position: relative;
    top: initial;
    order: 1;
    flex: initial;
    margin-left: auto;
    margin-top: 0;
    flex-direction: row;
  }
`
const StyledHeadingWrapper = styled.div`
  display: flex;
  order: 1;
  width: 100%;
  flex-direction: column;
  position: relative;

  ${({ theme }) => theme.mq.medium} {
    width: initial;
    flex-direction: row;
    align-items: baseline;
    margin-right: 0;
    margin-bottom: 1.6rem;
  }
`
const StyledCount = styled.span`
  font-size: ${({ theme }) => theme.font.size.s};
  line-height: 1.71;
  color: ${({ theme }) => theme.color.secondary02};
  margin-top: 0.4rem;

  ${({ theme }) => theme.mq.medium} {
    margin-left: 1.6rem;
    margin-top: 0;
  }
`
const StyledAutocomplete = styled(Autocomplete)`
  margin-bottom: 0;
  margin-right: 4rem;
  max-width: 29.5rem;
  width: 100%;

  input {
    height: 4.2rem;
    width: 100%;
    font-size: ${({ theme }) => theme.font.size.md};
  }

  ${({ theme }) => theme.mq.medium} {
    margin-right: 0.8rem;
  }

  ${({ theme }) => theme.mq.medium} {
    margin-right: 1.7rem;
    width: initial;
    flex: 1;
    max-width: initial;
  }
`

const StyledControlsWrapper = styled.div`
  display: flex;
  order: 2;
  margin-top: 1.6rem;
  width: 100%;

  ${({ theme }) => theme.mq.medium} {
    align-items: center;
    margin-right: 0;
    margin-top: 0;
  }
`
const dropdownStyles = css`
  padding-left: 0;
  width: 100%;
  margin: 0 0 1.6rem;

  > div {
    width: 100%;
  }

  ${({ theme }) => theme.mq.medium} {
    margin-bottom: 0;
  }
`

const StyledProjectTypeDropdown = styled(Dropdown)`
  ${dropdownStyles}

  ${({ theme }) => theme.mq.medium} {
    width: 16.2rem;
    margin-left: 0.8rem;
  }

  ${({ theme }) => theme.mq.large} {
    width: 16.6rem;
    margin-left: 1.7rem;
  }
`

const StyledPropertyTypeDropdown = styled(Dropdown)`
  ${dropdownStyles}

  ${({ theme }) => theme.mq.medium} {
    width: 17.2rem;
  }

  ${({ theme }) => theme.mq.large} {
    width: 17.9rem;
  }
`

const StyledStatusDropdown = styled(Dropdown)`
  ${dropdownStyles}

  ${({ theme }) => theme.mq.medium} {
    width: 21.2rem;
    margin: 0 0.8rem;
  }

  ${({ theme }) => theme.mq.large} {
    width: 23.2rem;
    margin: 0 1.7rem;
  }
`
const StyledFilterHeading = styled.span`
  font-family: ${({ theme }) => theme.font.family.headers};
  font-size: 2rem;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  line-height: 1.2;
  color: ${({ theme }) => theme.color.secondary01};
`
const StyledButtonsWrapper = styled.div`
  margin-top: 3.2rem;
  display: flex;
  justify-content: space-between;
`
const StyledBackButton = styled(Button)`
  width: 15.4rem;
  min-height: 4rem;
  height: 4rem;
  font-size: ${({ theme }) => theme.font.size.s};
  line-height: 1.71;
  letter-spacing: normal;
  padding: 0.8rem 1.4rem;

  ${({ theme }) => theme.mq.medium} {
    display: none;
  }
`

const StyledResetButton = styled(Button)`
  width: auto;
  min-height: 4rem;
  height: 4rem;
  font-size: ${({ theme }) => theme.font.size.s};
  line-height: 1.71;
  letter-spacing: normal;
  color: ${({ theme }) => theme.color.secondary01};
  padding: 0.8rem 1.4rem;

  ${({ theme }) => theme.mq.medium} {
    display: none;
  }
`
const StyledFiterHeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1.6rem;

  ${({ theme }) => theme.mq.medium} {
    display: none;
  }
`
export {
  StyledWrapper,
  projectDropdownStyles,
  StyledIcon,
  StyledLink,
  headingStyles,
  StyledDropdownWrapper,
  StyledHeadingWrapper,
  StyledCount,
  projectMiddleDropdownStyles,
  StyledAutocomplete,
  StyledControlsWrapper,
  StyledStatusDropdown,
  StyledProjectTypeDropdown,
  StyledPropertyTypeDropdown,
  StyledFilterButton,
  StyledFilterHeading,
  StyledBackButton,
  StyledFiterHeadingWrapper,
  StyledResetWrapper,
  StyledResetButton,
  StyledButtonsWrapper,
}


// Visually hides the heading from sighted users while keeping it in the
// accessible DOM order, so the page's heading hierarchy stays valid
// (h1 -> h2 -> h3/h4) without any visual change.
const visuallyHiddenStyles = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export { visuallyHiddenStyles }
